/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */
/**
 * SlideoutStackContext - Manages a stack of slideouts (CRM entities, proposals, etc.).
 *
 * Only the topmost slideout is rendered at any time. The context maintains
 * the full stack history so that closing the top slideout reveals the
 * previous one. The stack is synced bidirectionally with the URL param
 * `slideoutEntityId` (comma-separated) for deep linking and back/forward
 * navigation.
 *
 * Stack entries are typed so multiple kinds of slideouts (CRM entities,
 * proposals, ...) can share the same stack and stack on top of each other.
 *
 * URL format:
 *  - Entity entry: `<id>` (no prefix — backward compatible with original format)
 *  - Other kinds:  `<kind>:<id>` (e.g. `proposal:456`)
 *  - Stack: comma-separated parts, e.g. `uuid-123,proposal:456,uuid-456`
 *
 * @example
 * ```tsx
 * // In Root.tsx
 * <SlideoutStackProvider>
 *   <App />
 * </SlideoutStackProvider>
 *
 * // Push a CRM entity (backward compatible — string defaults to entity kind)
 * pushSlideout(object.entity_id);
 *
 * // Push a proposal
 * pushSlideout({ kind: 'proposal', id: String(proposalId) });
 * ```
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

// Direct module path, NOT the `lib/crm/hooks` barrel — the barrel re-exports
// useSlideoutStack, which imports this file (cycle). Same reason
// useNavigateToEntity imports this module directly.
import { TRANSIENT_FOCUS_PARAMS } from '../hooks/useFocusedAssociationScroll';

/** URL parameter name for the slideout stack */
export const SLIDEOUT_TIMELINE_ID_PARAM = 'slideoutEntityId';

/** Defensive limit: prevents unbounded memory from URL manipulation or API calls */
const MAX_SLIDEOUT_STACK_SIZE = 100;

/** Supported slideout kinds. Add new kinds here as they're introduced. */
export type SlideoutKind = 'entity' | 'proposal' | 'email_thread';

/** A single entry in the slideout stack. */
export interface SlideoutEntry {
  kind: SlideoutKind;
  id: string;
}

/** Coercion helper: accept legacy plain string/number (= entity) or typed entry. */
export type SlideoutPushInput = string | number | SlideoutEntry;

function toEntry(input: SlideoutPushInput): SlideoutEntry {
  if (typeof input === 'string' || typeof input === 'number') {
    return { kind: 'entity', id: String(input) };
  }
  return { kind: input.kind, id: String(input.id) };
}

function entriesEqual(
  a: SlideoutEntry | undefined,
  b: SlideoutEntry | undefined
): boolean {
  if (!a || !b) {
    return a === b;
  }
  return a.kind === b.kind && a.id === b.id;
}

/**
 * Collapse runs of consecutive identical entries: [A,A]->[A], [A,A,A]->[A],
 * [A,B,B,A]->[A,B,A]. Non-consecutive duplicates ([A,B,A]) are preserved —
 * they are legitimate back-navigation history; only consecutive duplicates
 * are meaningless (popping one reveals an identical view).
 */
function collapseConsecutiveDuplicates(
  entries: SlideoutEntry[]
): SlideoutEntry[] {
  const result: SlideoutEntry[] = [];
  for (const entry of entries) {
    if (!entriesEqual(result[result.length - 1], entry)) {
      result.push(entry);
    }
  }
  return result;
}

/** Index of the LAST entry equal to `target`, or -1 when absent. */
function findLastIndexOfEntry(
  entries: SlideoutEntry[],
  target: SlideoutEntry
): number {
  for (let i = entries.length - 1; i >= 0; i--) {
    if (entriesEqual(entries[i], target)) {
      return i;
    }
  }
  return -1;
}

/** True when both stacks hold equal entries in the same order. */
function stacksEqual(a: SlideoutEntry[], b: SlideoutEntry[]): boolean {
  return (
    a.length === b.length && a.every((entry, i) => entriesEqual(entry, b[i]))
  );
}

/**
 * The stack that results from an app-level open (`openSlideout`): everything
 * above the LAST entry equal to `base` is discarded (the whole stack when
 * `base` is absent or not found), then `entry` goes on top — unless it is
 * already the kept top, in which case the kept stack stands as-is.
 */
function computeOpenedStack(
  prev: SlideoutEntry[],
  base: SlideoutEntry | undefined,
  entry: SlideoutEntry
): SlideoutEntry[] {
  const baseIdx = base ? findLastIndexOfEntry(prev, base) : -1;
  // slice(0, 0) when the base is absent/not found — the whole stack goes.
  const kept = prev.slice(0, baseIdx + 1);
  if (entriesEqual(kept[kept.length - 1], entry)) {
    return kept;
  }
  return [...kept, entry];
}

/**
 * Apply extra URL search params in place: set non-empty values, delete keys
 * whose value is the empty string (used to clear a stale value).
 */
function applyExtraParams(
  params: URLSearchParams,
  extra: Record<string, string>
): void {
  for (const [key, value] of Object.entries(extra)) {
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
  }
}

export interface PushSlideoutOptions {
  /** Extra URL search params to set alongside the slideout (e.g., { focusedSection: 'meeting_prep' }) */
  searchParams?: Record<string, string>;
}

export interface OpenSlideoutOptions extends PushSlideoutOptions {
  /**
   * The stack entry the opened slideout should sit directly on top of —
   * typically the slideout hosting the clicked button (e.g. the proposal
   * slideout whose header "View X" button was clicked). Everything above the
   * LAST matching entry is discarded before the new entry is placed; when
   * absent or not found in the stack, the entire stack is replaced.
   */
  base?: SlideoutPushInput;
}

/**
 * Guard function type for push navigation.
 * Called before pushing a new slideout onto the stack.
 * Returns true if the push should proceed immediately, false if it's blocked (e.g. showing a modal).
 */
export type PushGuardFn = (action: () => void) => boolean;

export interface SlideoutStackContextValue {
  /** The full stack of entries (bottom to top) */
  stack: SlideoutEntry[];
  /** The top (currently visible) entry, or undefined if stack is empty */
  topEntry: SlideoutEntry | undefined;
  /**
   * The top object ID, or undefined if stack is empty OR the top entry is not
   * a CRM entity. Preserved for backward compatibility — new code should
   * prefer `topEntry`.
   */
  topId: string | undefined;
  /** Whether any slideout is open */
  isOpen: boolean;
  /**
   * Push a slideout onto the stack.
   *
   * Accepts legacy string/number (treated as entity kind) or a typed
   * `SlideoutEntry`. Duplicates are allowed for full back-navigation.
   */
  pushSlideout: (
    input: SlideoutPushInput,
    options?: PushSlideoutOptions
  ) => void;
  /**
   * App-level open. The product rule: a click FROM THE APP (a page, a
   * header, a docked sidebar) REPLACES whatever slideouts are layered; only
   * a click from INSIDE a slideout builds the stack (via `pushSlideout`).
   *
   * Discards everything layered above `options.base` — the whole stack when
   * no base is given or found — and shows the given slideout on top. Routed
   * through the push guard exactly like `pushSlideout`, so unsaved changes
   * in the currently-open slideout still intercept.
   */
  openSlideout: (
    input: SlideoutPushInput,
    options?: OpenSlideoutOptions
  ) => void;
  /** Pop the top entry from the stack */
  popSlideout: () => void;
  /**
   * Replace the top entry without changing stack depth. Used for sequential
   * transitions (e.g. auto-advancing to the next proposal after approve)
   * where back-navigation should NOT return to the replaced entry.
   *
   * No-op when the stack is empty (use `pushSlideout` to start a stack).
   */
  replaceTop: (input: SlideoutPushInput) => void;
  /** Clear the entire stack */
  clearStack: () => void;
  /**
   * Close the slideout for `input` and everything layered above it. Finds
   * the LAST entry in the stack matching the input's kind+id and truncates
   * the stack at that index (entries below it — e.g. a `proposal` base on
   * the chat surface — stay). No-op when no entry matches. Routed through
   * the push guard exactly like `openSlideout`, so unsaved changes in the
   * currently-open slideout still intercept.
   */
  closeFromEntry: (input: SlideoutPushInput) => void;
  /**
   * Dismiss the record/thread layers while preserving a proposal underneath:
   * truncates the stack to (and including) the TOPMOST `proposal` entry —
   * everything layered above that proposal is removed, and everything at or
   * below it (including any slideouts beneath the proposal) stays visible.
   * So closing an email thread opened over a proposal that is itself layered
   * over another slideout returns to the proposal, not to an empty stack.
   * Identical to `clearStack` when no proposal is on the stack. Guard-routed
   * like `openSlideout` — for callers that have NOT already resolved a local
   * unsaved-changes guard (e.g. `EmailThreadPanel`, which has none of its
   * own and relies on this to check whatever surface is still registered).
   */
  clearToProposalBase: () => void;
  /**
   * Same truncation as `clearToProposalBase`, but unguarded — for hosts
   * (`CrmObjectSlideout`, `InlineCrmObjectPanel`) that already resolved
   * their own local unsaved-changes guard via `useGuardedDismiss` before
   * calling this as the `onClose` action. Routing those through the guarded
   * `clearToProposalBase` double-guards: the host's own guard already
   * showed the confirmation and the user already chose to proceed, so a
   * second check against the same still-registered guard re-opens the
   * modal instead of closing. Mirrors `popSlideout`, which is unguarded for
   * the same reason.
   */
  dismissToProposalBase: () => void;
  /** Replace the entire stack (for direct URL param patterns) */
  setStack: (entries: SlideoutEntry[]) => void;
  /** Register a guard that intercepts pushSlideout when there are unsaved changes */
  registerPushGuard: (guard: PushGuardFn) => void;
  /** Unregister the push guard */
  unregisterPushGuard: () => void;
  /**
   * Kinds currently claimed by an INLINE HOST — a page that renders these
   * slideout kinds as a real child of its own layout (e.g. the action detail
   * page's SplitView entity panel). The global SlideoutRenderer stands down
   * for claimed kinds; everything else about the stack (URL sync, push/pop,
   * guards) behaves identically.
   */
  inlineHostKinds: ReadonlySet<SlideoutKind>;
  /**
   * Claim inline hosting for the given kinds. Returns an unregister function
   * — call it (or let the effect cleanup call it) when the host unmounts or
   * stops being eligible (e.g. the viewport gets too narrow), so the global
   * renderer takes over again.
   */
  registerInlineHost: (kinds: SlideoutKind[]) => () => void;
}

const SlideoutStackContext = createContext<SlideoutStackContextValue | null>(
  null
);

/**
 * Hook to access the slideout stack context.
 * Must be used within a SlideoutStackProvider.
 */
export function useSlideoutStackContext(): SlideoutStackContextValue {
  const context = useContext(SlideoutStackContext);
  if (!context) {
    throw new Error(
      'useSlideoutStackContext must be used within a SlideoutStackProvider'
    );
  }
  return context;
}

/**
 * Optional variant that returns null when outside the provider.
 * Useful in tests or components that may render outside the provider.
 */
export function useSlideoutStackContextOptional(): SlideoutStackContextValue | null {
  return useContext(SlideoutStackContext);
}

/** Parse a single URL token (e.g. `uuid-123` or `proposal:456`) into an entry. */
function parseToken(token: string): SlideoutEntry | null {
  const trimmed = token.trim();
  if (!trimmed) {
    return null;
  }
  const colonIdx = trimmed.indexOf(':');
  // Tokens without a colon are legacy entity entries.
  if (colonIdx === -1) {
    return { kind: 'entity', id: trimmed };
  }
  const kindPart = trimmed.slice(0, colonIdx);
  const idPart = trimmed.slice(colonIdx + 1);
  if (!idPart) {
    return null;
  }
  // Whitelist supported kinds. Unknown kinds are dropped to avoid leaking
  // arbitrary data into the stack from URL manipulation.
  if (
    kindPart === 'entity' ||
    kindPart === 'proposal' ||
    kindPart === 'email_thread'
  ) {
    return { kind: kindPart, id: idPart };
  }
  return null;
}

/** Serialize one entry to its URL token form. */
function serializeEntry(entry: SlideoutEntry): string {
  // Entity entries omit the prefix for backward compatibility with the
  // original `slideoutEntityId=<id>` URL format.
  if (entry.kind === 'entity') {
    return entry.id;
  }
  return `${entry.kind}:${entry.id}`;
}

/** Parse the slideout stack from URL search params */
function parseStackFromParams(params: URLSearchParams): SlideoutEntry[] {
  const param = params.get(SLIDEOUT_TIMELINE_ID_PARAM);
  if (!param) {
    return [];
  }
  const entries = collapseConsecutiveDuplicates(
    param
      .split(',')
      .map(parseToken)
      .filter((e): e is SlideoutEntry => e !== null)
  );
  if (entries.length > MAX_SLIDEOUT_STACK_SIZE) {
    console.warn(
      `SlideoutStack from URL exceeded maximum size (${entries.length} items), truncating to most recent ${MAX_SLIDEOUT_STACK_SIZE} items`
    );
    return entries.slice(-MAX_SLIDEOUT_STACK_SIZE);
  }
  return entries;
}

/** Serialize stack to URL param value, or null if empty */
function serializeStack(stack: SlideoutEntry[]): string | null {
  if (stack.length === 0) {
    return null;
  }
  return stack.map(serializeEntry).join(',');
}

export function SlideoutStackProvider({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Mirror of setSearchParams so imperative callbacks stay reference-stable even
  // though react-router recreates the setter when the query string changes.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  // Initialize stack from URL on mount
  const [stack, setStackState] = useState<SlideoutEntry[]>(() =>
    parseStackFromParams(searchParams)
  );

  // Mirror of the latest committed stack so imperative callbacks (doPush) can
  // read the current top without being recreated on every stack change.
  const stackRef = useRef(stack);
  stackRef.current = stack;

  // Track whether we caused the URL change (to prevent sync loops)
  const syncingToUrlRef = useRef(false);

  // Track the last action type to determine replace vs push for browser history
  const lastActionRef = useRef<'push' | 'pop' | 'clear' | 'set' | 'replace'>(
    'set'
  );

  // Push guard: intercepts pushSlideout when the current slideout has unsaved changes
  const pushGuardRef = useRef<PushGuardFn | null>(null);

  // Track the last serialized stack we synced to URL, to avoid redundant updates.
  // Seed from the RAW url param (not the collapsed serialization) so a polluted
  // URL self-heals on mount: when the raw param (`A,A`) differs from the canonical
  // serialization of the parsed stack (`A`), the state->URL sync effect below sees
  // the mismatch and rewrites the param once (with replace, since lastActionRef
  // starts as 'set'). A clean URL seeds equal and skips the redundant write.
  const lastSyncedRef = useRef<string | null>(
    searchParams.get(SLIDEOUT_TIMELINE_ID_PARAM)
  );

  // Pending extra search params to apply atomically with the next URL sync
  const pendingSearchParamsRef = useRef<Record<string, string> | null>(null);

  // Write extra URL search params directly. Needed when a push is collapsed as a
  // consecutive duplicate: the stack does not change, so the stack->URL sync
  // effect never fires and would otherwise drop the caller's params (e.g.
  // meeting-prep's { focusedSection: 'meeting_prep' }).
  const applyExtraSearchParams = useCallback(
    (extra: Record<string, string>) => {
      setSearchParamsRef.current(
        prev => {
          const params = new URLSearchParams(prev);
          applyExtraParams(params, extra);
          return params;
        },
        { replace: true }
      );
    },
    []
  );

  // Internal push implementation (no guard check)
  const doPush = useCallback(
    (entry: SlideoutEntry, options?: PushSlideoutOptions) => {
      // Collapse consecutive duplicates: pushing the entry already on top is
      // meaningless and is the root cause of the stuck-slideout bug. Skip the
      // append, but still honor extra search params (e.g. a tab change on the
      // already-open entity).
      if (entriesEqual(stackRef.current[stackRef.current.length - 1], entry)) {
        if (options?.searchParams) {
          applyExtraSearchParams(options.searchParams);
        }
        return;
      }
      lastActionRef.current = 'push';
      if (options?.searchParams) {
        // Only reached on a genuine (non-duplicate) append; the effect applies
        // these atomically with the new slideout param.
        pendingSearchParamsRef.current = options.searchParams;
      }
      setStackState(prev => {
        // Same-tick safety net: if two pushes of the same id fire before stackRef
        // catches up, the updater still refuses the duplicate so [A, A] can never
        // enter the stack.
        if (entriesEqual(prev[prev.length - 1], entry)) {
          return prev;
        }
        const newStack = [...prev, entry];
        if (newStack.length > MAX_SLIDEOUT_STACK_SIZE) {
          console.warn(
            `SlideoutStack exceeded maximum size (${newStack.length} items), truncating to most recent ${MAX_SLIDEOUT_STACK_SIZE} items`
          );
          return newStack.slice(-MAX_SLIDEOUT_STACK_SIZE);
        }
        return newStack;
      });
    },
    [applyExtraSearchParams]
  );

  // Internal app-level open implementation (no guard check). See openSlideout.
  const doOpen = useCallback(
    (
      entry: SlideoutEntry,
      base: SlideoutEntry | undefined,
      options?: PushSlideoutOptions
    ) => {
      // Nothing to change (the target is already the visible top of the kept
      // stack) — but still honor extra search params, mirroring doPush's
      // consecutive-duplicate path.
      if (
        stacksEqual(
          computeOpenedStack(stackRef.current, base, entry),
          stackRef.current
        )
      ) {
        if (options?.searchParams) {
          applyExtraSearchParams(options.searchParams);
        }
        return;
      }
      // 'push' so the open creates a browser-history entry, matching what a
      // pushSlideout from the same click produced before openSlideout existed.
      lastActionRef.current = 'push';
      if (options?.searchParams) {
        pendingSearchParamsRef.current = options.searchParams;
      }
      setStackState(prev => {
        // Recompute from `prev` (same-tick safety, like doPush's updater).
        const next = computeOpenedStack(prev, base, entry);
        if (stacksEqual(next, prev)) {
          return prev;
        }
        // Growth is at most kept-prefix + 1, but keep the defensive cap.
        return next.length > MAX_SLIDEOUT_STACK_SIZE
          ? next.slice(-MAX_SLIDEOUT_STACK_SIZE)
          : next;
      });
    },
    [applyExtraSearchParams]
  );

  // App-level open: replace everything above options.base (or the whole
  // stack) with the entry. Guard-checked exactly like pushSlideout.
  const openSlideout = useCallback(
    (input: SlideoutPushInput, options?: OpenSlideoutOptions) => {
      const entry = toEntry(input);
      const base = options?.base != null ? toEntry(options.base) : undefined;
      const guard = pushGuardRef.current;

      if (guard) {
        // Guard returns true if the open should proceed, false if blocked
        // (modal shown); when blocked the guard calls the action after the
        // user confirms.
        const shouldProceed = guard(() => doOpen(entry, base, options));
        if (shouldProceed) {
          doOpen(entry, base, options);
        }
      } else {
        doOpen(entry, base, options);
      }
    },
    [doOpen]
  );

  // Push an entry onto the stack (duplicates allowed for full back-navigation)
  // If a push guard is registered and returns false, the push is deferred until the guard resolves.
  const pushSlideout = useCallback(
    (input: SlideoutPushInput, options?: PushSlideoutOptions) => {
      const entry = toEntry(input);
      const guard = pushGuardRef.current;

      if (guard) {
        // Guard returns true if push should proceed, false if blocked (modal shown)
        const shouldProceed = guard(() => doPush(entry, options));
        if (shouldProceed) {
          doPush(entry, options);
        }
        // If false, the guard will call doPush(entry, options) via the action callback after user confirms
      } else {
        doPush(entry, options);
      }
    },
    [doPush]
  );

  // Pop the top entry from the stack
  const popSlideout = useCallback(() => {
    lastActionRef.current = 'pop';
    setStackState(prev => {
      if (prev.length === 0) {
        return prev;
      }
      return prev.slice(0, -1);
    });
  }, []);

  // Replace the top entry without changing stack depth.
  const replaceTop = useCallback((input: SlideoutPushInput) => {
    const entry = toEntry(input);
    lastActionRef.current = 'replace';
    setStackState(prev => {
      if (prev.length === 0) {
        return prev;
      }
      const next = prev.slice(0, -1);
      next.push(entry);
      return next;
    });
  }, []);

  // Clear the entire stack
  const clearStack = useCallback(() => {
    lastActionRef.current = 'clear';
    setStackState([]);
  }, []);

  // Internal closeFromEntry implementation (no guard check). See closeFromEntry.
  const doCloseFromEntry = useCallback((entry: SlideoutEntry) => {
    lastActionRef.current = 'pop';
    setStackState(prev => {
      const idx = findLastIndexOfEntry(prev, entry);
      if (idx === -1) {
        return prev;
      }
      return prev.slice(0, idx);
    });
  }, []);

  // Close the slideout for `input` and everything layered above it. See the
  // doc comment on SlideoutStackContextValue.closeFromEntry.
  const closeFromEntry = useCallback(
    (input: SlideoutPushInput) => {
      const entry = toEntry(input);
      const guard = pushGuardRef.current;

      if (guard) {
        const shouldProceed = guard(() => doCloseFromEntry(entry));
        if (shouldProceed) {
          doCloseFromEntry(entry);
        }
      } else {
        doCloseFromEntry(entry);
      }
    },
    [doCloseFromEntry]
  );

  // Internal clearToProposalBase implementation (no guard check). See
  // clearToProposalBase.
  const doClearToProposalBase = useCallback(() => {
    lastActionRef.current = 'clear';
    setStackState(prev => {
      // Keep everything up to and INCLUDING the topmost proposal entry, so a
      // record/thread layered above a proposal reveals that proposal — even
      // when the proposal itself sits above another slideout. Only a leading-
      // run scan would have dropped the whole stack in that case. With no
      // proposal present this truncates to empty (identical to clearStack).
      let lastProposalIdx = -1;
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].kind === 'proposal') {
          lastProposalIdx = i;
          break;
        }
      }
      const keepLength = lastProposalIdx + 1;
      return keepLength === prev.length ? prev : prev.slice(0, keepLength);
    });
  }, []);

  // Dismiss the record/thread layers while preserving a proposal base. See
  // the doc comment on SlideoutStackContextValue.clearToProposalBase.
  const clearToProposalBase = useCallback(() => {
    const guard = pushGuardRef.current;

    if (guard) {
      const shouldProceed = guard(() => doClearToProposalBase());
      if (shouldProceed) {
        doClearToProposalBase();
      }
    } else {
      doClearToProposalBase();
    }
  }, [doClearToProposalBase]);

  // Register a guard that intercepts pushSlideout
  const registerPushGuard = useCallback((guard: PushGuardFn) => {
    pushGuardRef.current = guard;
  }, []);

  // Unregister the push guard
  const unregisterPushGuard = useCallback(() => {
    pushGuardRef.current = null;
  }, []);

  // Replace the entire stack
  const setStack = useCallback((entries: SlideoutEntry[]) => {
    lastActionRef.current = 'set';
    const collapsed = collapseConsecutiveDuplicates(entries);
    if (collapsed.length > MAX_SLIDEOUT_STACK_SIZE) {
      console.warn(
        `SlideoutStack API call exceeded maximum size (${collapsed.length} items), truncating to most recent ${MAX_SLIDEOUT_STACK_SIZE} items`
      );
      setStackState(collapsed.slice(-MAX_SLIDEOUT_STACK_SIZE));
      return;
    }
    setStackState(collapsed);
  }, []);

  // Sync context state -> URL param
  useEffect(() => {
    const serialized = serializeStack(stack);

    // Skip if we already synced this value
    if (serialized === lastSyncedRef.current) {
      // Clear pending params to prevent leak when sync short-circuits
      pendingSearchParamsRef.current = null;
      return;
    }
    lastSyncedRef.current = serialized;

    syncingToUrlRef.current = true;

    // Use replace for pop/clear/set/replace, push for push (creates history entry)
    const replace = lastActionRef.current !== 'push';

    // Grab and clear any pending extra search params
    const extraParams = pendingSearchParamsRef.current;
    pendingSearchParamsRef.current = null;

    setSearchParams(
      prev => {
        const params = new URLSearchParams(prev);
        if (serialized) {
          params.set(SLIDEOUT_TIMELINE_ID_PARAM, serialized);
        } else {
          params.delete(SLIDEOUT_TIMELINE_ID_PARAM);
          // Backstop for transient focus params. The receiving section
          // normally consumes its own instruction on arrival, but it can never
          // get the chance if it doesn't mount — e.g. the user closes the
          // slideout before the prep query resolves. With no slideout open
          // there is no instruction left to honor, so sweep them.
          //
          // Scoped to "stack emptied", not "any pop": popping one level of a
          // nested stack must leave a pending instruction for the revealed
          // slideout intact. This derives purely from `stack.length === 0`,
          // which already round-trips through the URL, so it can't desync on
          // back/forward.
          for (const key of TRANSIENT_FOCUS_PARAMS) {
            params.delete(key);
          }
        }
        // Apply extra search params atomically (e.g., ?focusedSection=meeting_prep)
        // Empty string values delete the param (used to clear stale values)
        if (extraParams) {
          applyExtraParams(params, extraParams);
        }
        return params;
      },
      { replace }
    );

    // Reset the syncing flag after React processes the URL change
    requestAnimationFrame(() => {
      syncingToUrlRef.current = false;
    });
  }, [stack, setSearchParams]);

  // Sync URL -> context (for back/forward button navigation)
  useEffect(() => {
    // Skip if we caused this URL change
    if (syncingToUrlRef.current) {
      return;
    }

    const urlStack = parseStackFromParams(searchParams);
    const urlSerialized = serializeStack(urlStack);
    const currentSerialized = serializeStack(stack);

    // Only update if different
    if (urlSerialized !== currentSerialized) {
      lastActionRef.current = 'set';
      lastSyncedRef.current = urlSerialized;
      setStackState(urlStack);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally excluding stack to avoid loops

  // Inline-host claims. A Map of registration-token → kinds supports the
  // (unusual but possible) overlap of two hosts during a route transition —
  // a kind stays claimed while ANY registration covers it.
  const [inlineHostClaims, setInlineHostClaims] = useState<
    ReadonlyMap<symbol, SlideoutKind[]>
  >(new Map());
  const registerInlineHost = useCallback((kinds: SlideoutKind[]) => {
    const token = Symbol('inline-host');
    setInlineHostClaims(prev => {
      const next = new Map(prev);
      next.set(token, kinds);
      return next;
    });
    return () => {
      setInlineHostClaims(prev => {
        if (!prev.has(token)) {
          return prev;
        }
        const next = new Map(prev);
        next.delete(token);
        return next;
      });
    };
  }, []);
  const inlineHostKinds = useMemo(() => {
    const kinds = new Set<SlideoutKind>();
    for (const claimed of inlineHostClaims.values()) {
      for (const kind of claimed) {
        kinds.add(kind);
      }
    }
    return kinds;
  }, [inlineHostClaims]);

  const value: SlideoutStackContextValue = useMemo(() => {
    const topEntry = stack.length > 0 ? stack[stack.length - 1] : undefined;
    return {
      stack,
      topEntry,
      // Backward compatibility: topId only resolves when top is an entity.
      // Callers that need proposal IDs should read topEntry instead.
      topId: topEntry && topEntry.kind === 'entity' ? topEntry.id : undefined,
      isOpen: stack.length > 0,
      pushSlideout,
      openSlideout,
      popSlideout,
      replaceTop,
      clearStack,
      closeFromEntry,
      clearToProposalBase,
      dismissToProposalBase: doClearToProposalBase,
      setStack,
      registerPushGuard,
      unregisterPushGuard,
      inlineHostKinds,
      registerInlineHost,
    };
  }, [
    stack,
    pushSlideout,
    openSlideout,
    popSlideout,
    replaceTop,
    clearStack,
    closeFromEntry,
    clearToProposalBase,
    doClearToProposalBase,
    setStack,
    registerPushGuard,
    unregisterPushGuard,
    inlineHostKinds,
    registerInlineHost,
  ]);

  return <SlideoutStackContext value={value}>{children}</SlideoutStackContext>;
}

// Exported for tests and helpers that want to compare entries.
export { entriesEqual, parseToken, serializeEntry, toEntry };
